import { Button } from '@optools/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import { Link, LinkProps, useMatchRoute } from '@tanstack/react-router';
import { LucideProps } from 'lucide-react';
import { useState } from 'react';

interface BaseSidebarLinkProps {
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
  >;
  name: string;
  sidebarOpen: boolean;
}

export type SidebarLinkProps = BaseSidebarLinkProps &
  (
    | {
        external: true;
        activePath?: string;
        to: string;
      }
    | {
        external?: false;
        to: Exclude<LinkProps['to'], '.' | '..'>;
        activePath?: LinkProps['to'];
      }
  );

export function SidebarLink({
  to,
  activePath = to,
  external,
  icon,
  name,
  sidebarOpen,
}: SidebarLinkProps) {
  const Icon = icon;

  const [open, setOpen] = useState(false);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!sidebarOpen) setOpen(nextOpen);
  };

  const matchRoute = useMatchRoute();
  const isActive = matchRoute({ fuzzy: true, to: activePath });

  const content = (
    <>
      <Icon className="size-5 shrink-0" />
      <span
        className={cn(
          'linear transition-opacity duration-300',
          sidebarOpen ? 'opacity-100 delay-100' : 'opacity-0',
        )}>
        {name}
      </span>
    </>
  );

  return (
    <Tooltip open={open} onOpenChange={handleOpenChange}>
      <TooltipTrigger asChild>
        <Button
          aria-label={name}
          asChild
          className={cn(
            'justify-start gap-2 overflow-hidden pl-2 transition-all duration-300',
            sidebarOpen ? 'w-full' : 'w-auto',
          )}
          size="icon-lg"
          variant="ghost">
          {external ? (
            <a href={to} rel="noreferrer" target="_blank">
              {content}
            </a>
          ) : (
            <Link className={cn(!!isActive && 'bg-muted')} from="/" to={to}>
              {content}
            </Link>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={5}>
        {name}
      </TooltipContent>
    </Tooltip>
  );
}
